import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Clock, 
  Shield, 
  Heart, 
  Brain, 
  Calendar,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';

const Participate = () => {
  
  const eligibilityCriteria = [
    {
      icon: Calendar,
      title: 'Age Requirement',
      description: 'Must be between 18-29 years old at the time of enrollment'
    },
    {
      icon: MapPin,
      title: 'Location',
      description: 'Must be able to travel to one of our study sites for assessments'
    },
    {
      icon: Heart,
      title: 'Health Status',
      description: 'No major health conditions that would prevent participation'
    },
    {
      icon: Shield,
      title: 'Consent',
      description: 'Willing to provide informed consent and participate for 15 years'
    }
  ];

  const studyProcess = [
    {
      step: 1,
      title: 'Initial Contact',
      description: 'Complete our online interest form or call our recruitment team',
      duration: '5 minutes'
    },
    {
      step: 2,
      title: 'Screening',
      description: 'Brief phone screening to confirm eligibility and answer questions',
      duration: '15 minutes'
    },
    {
      step: 3,
      title: 'Consent & Enrollment',
      description: 'Review study details and provide informed consent',
      duration: '30 minutes'
    },
    {
      step: 4,
      title: 'Baseline Assessment',
      description: 'Complete comprehensive health assessment including MRI and blood tests',
      duration: '4-6 hours'
    },
    {
      step: 5,
      title: 'Follow-up Assessment',
      description: 'Second assessment 2-3 years later',
      duration: '4-6 hours'
    },
    {
      step: 6,
      title: 'Long-term Follow-up',
      description: 'Annual health updates through electronic records for 15 years',
      duration: 'Ongoing'
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: 'Free Comprehensive Health Assessment',
      description: 'Receive detailed information about your health including advanced MRI scans, blood tests, and cognitive assessments worth thousands of dollars'
    },
    {
      icon: Brain,
      title: 'Early Health Insights',
      description: 'Learn about your health status and receive personalized feedback about your risk factors and health recommendations'
    },
    {
      icon: Shield,
      title: 'Contribute to Medical Research',
      description: 'Help advance medical science and potentially save lives by contributing to groundbreaking health research'
    },
    {
      icon: CheckCircle,
      title: 'Long-term Health Monitoring',
      description: 'Benefit from ongoing health monitoring through electronic health records for 15 years'
    }
  ];

  return (
    <div className="participate">
      {/* Hero Section */}
      <section
        className="hero"
        style={{
          paddingTop: "120px",
          backgroundImage:
            "linear-gradient(135deg, rgba(30, 64, 175, 0.9) 0%, rgba(59, 130, 246, 0.9) 100%), url(/images/young-adults.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "white",
        }}
      >
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 style={{ color: "white" }}>
              Join Canada's Most Comprehensive Health Study
            </h1>
            <p style={{ color: "rgba(255, 255, 255, 0.9)" }}>
              Help us understand and prevent chronic diseases affecting young
              adults. Get a free comprehensive health assessment while
              contributing to groundbreaking research.
            </p>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-number">FREE</span>
                <span className="hero-stat-label">Health Assessment</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-number">20</span>
                <span className="hero-stat-label">Years Follow-up</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-number">2,000</span>
                <span className="hero-stat-label">Phase 1 Goal</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Eligibility Section */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Are You Eligible?</h2>
            <p className="section-subtitle">
              To participate in the Canadian Biobank, you must meet the
              following criteria:
            </p>
            <div className="grid grid-4" style={{ marginTop: "2rem" }}>
              {eligibilityCriteria.map((criteria, index) => (
                <motion.div
                  key={criteria.title}
                  className="card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <criteria.icon
                    size={32}
                    style={{ color: "#1E40AF", marginBottom: "1rem" }}
                  />
                  <h3 style={{ marginBottom: "0.5rem" }}>{criteria.title}</h3>
                  <p>{criteria.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Study Process */}
      <section className="section" style={{ background: "#f8fafc" }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">What to Expect</h2>
            <p className="section-subtitle">
              Here's what your participation in the study will involve:
            </p>
            <div style={{ marginTop: "3rem" }}>
              {studyProcess.map((step, index) => (
                <motion.div
                  key={step.step}
                  className="process-step"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "2rem",
                    padding: "1.5rem",
                    background: "white",
                    borderRadius: "12px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                      marginRight: "1.5rem",
                      flexShrink: 0,
                    }}
                  >
                    {step.step}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ color: "#1E40AF", marginBottom: "0.5rem" }}>
                      {step.title}
                    </h3>
                    <p style={{ marginBottom: "0.5rem" }}>{step.description}</p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: "#666",
                      }}
                    >
                      <Clock size={16} />
                      <span style={{ fontSize: "0.9rem" }}>
                        {step.duration}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Benefits of Participation</h2>
            <div className="grid grid-4" style={{ marginTop: "2rem" }}>
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  className="card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <benefit.icon
                    size={32}
                    style={{ color: "#1E40AF", marginBottom: "1rem" }}
                  />
                  <h3 style={{ marginBottom: "0.5rem" }}>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form - temporarily disabled
      <section className="section" style={{ background: "#f8fafc" }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Express Your Interest</h2>
            <p className="section-subtitle">
              Ready to join our study? Fill out the form below and our team will
              contact you to discuss your participation.
            </p>

            <div
              style={{ 
                maxWidth: "100%", 
                margin: "0 auto", 
                marginTop: "2rem",
                padding: "0 1rem",
                width: "100%"
              }}
            >
              <div
                className="card"
                style={{ 
                  padding: "0", 
                  overflow: "hidden",
                  maxWidth: "100%",
                  width: "100%",
                  boxSizing: "border-box"
                }}
              >
                <iframe 
                  src="https://docs.google.com/forms/d/e/1FAIpQLSegQOxBTORhL1GpGuqil-a8L33SsiX5_i9yOKGVqXs1UXqFYQ/viewform?embedded=true"
                  width="100%"
                  height={iframeHeight}
                  frameBorder="0"
                  marginHeight="0"
                  marginWidth="0"
                  title="Canadian Biobank Interest Form"
                  style={{
                    border: "none",
                    borderRadius: "12px",
                    minHeight: "600px",
                    width: "100%",
                    maxWidth: "100%",
                    display: "block",
                    transition: "height 0.3s ease"
                  }}
                >
                  Loading...
                </iframe>
              </div>

              <div
                style={{
                  marginTop: "1rem",
                  textAlign: "center",
                  color: "#666",
                  fontSize: "0.9rem",
                }}
              >
                <p>Having trouble with the form? Contact us directly:</p>
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:info@canadianbiobank.ca">
                    info@canadianbiobank.ca
                  </a>{" "}
                  |<strong>Phone:</strong>{" "}
                  <a href="tel:1-800-CBP-HELP">1-800-CBP-HELP</a>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      */}

      {/* Contact Information */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="section-title">Have Questions?</h2>
            <p className="section-subtitle">
              Our research team is here to answer any questions you may have
              about the study.
            </p>
            <div className="grid grid-3" style={{ marginTop: "2rem" }}>
              <div className="card text-center">
                <Phone
                  size={32}
                  style={{ color: "#1E40AF", marginBottom: "1rem" }}
                />
                <h3>Call Us</h3>
                <p>1-800-CBP-HELP</p>
                <p style={{ fontSize: "0.9rem", color: "#666" }}>
                  Monday-Friday, 9AM-5PM
                </p>
              </div>
              <div className="card text-center">
                <Mail
                  size={32}
                  style={{ color: "#1E40AF", marginBottom: "1rem" }}
                />
                <h3>Email Us</h3>
                <p>info@canadianbiobank.ca</p>
                <p style={{ fontSize: "0.9rem", color: "#666" }}>
                  We respond within 24 hours
                </p>
              </div>
              <div className="card text-center">
                <MapPin
                  size={32}
                  style={{ color: "#1E40AF", marginBottom: "1rem" }}
                />
                <h3>Visit Us</h3>
                <p>University of Waterloo</p>
                <p style={{ fontSize: "0.9rem", color: "#666" }}>
                  School of Public Health Sciences
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Participate;
